import * as React from 'react';
import { MAP_ID } from 'maps/mapsConfig';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { Theme, css } from '@emotion/react';
import { useState } from 'react';
import _ from 'lodash';

import {
  BusinessEntity,
  ConsumerPurchaseSummary,
  PatronEntity,
  TicketTypeEntity,
} from 'generated/graphql';
import { LocationPurchaseHistory } from 'selectors/locationGroupedPurchaseHistorySelector';
import { getLatLong } from 'utils/locationUtils';
import { isTouchScreen } from 'utils/deviceUtils';
import FullscreenControl from 'components/profile/culinary-travel-log/FullscreenControl';
import MapsApiProvider from 'maps/MapsApiProvider';
import RestaurantMarkerNew from 'components/profile/culinary-travel-log/RestaurantMarkerNew';
import ZoomControls from 'components/profile/culinary-travel-log/ZoomControls';

export interface ProfileMapNewProps {
  locationPurchaseHistory: LocationPurchaseHistory[];
  patronZip?: string;
  patronCity?: string;
  isMapLoaded: boolean;
  loadError?: Error;
}

// Weirdly, replacing this with a literal causes strange map behavior on pin selection
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 44.076613, lng: -98.362239833 };

/**
 * Inner controller rendered under the `MapsApiProvider`. Uses `useMap()` to fit
 * the map to the purchase locations and to drive the fullscreen/zoom controls.
 *
 * Note: this map uses the Advanced-Marker `mapId` (required by
 * `RestaurantMarkerNew`'s `<AdvancedMarker>`), which is incompatible with the
 * legacy inline `styles` array (`mapStyle`) — cloud styling on the map ID
 * replaces it. This is the sanctioned styles-vs-mapId tradeoff for the vis.gl
 * path.
 */
const ProfileMapController: React.FunctionComponent<ProfileMapNewProps> = ({
  locationPurchaseHistory,
  patronZip,
  patronCity,
  loadError,
}) => {
  const map = useMap();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | undefined>(undefined);
  const isTouch = isTouchScreen();
  const hasInitialized = React.useRef(false);

  const handleOpen = (markerId: string) => {
    setSelectedMarkerId(undefined);
    setSelectedMarkerId(markerId);
  };

  const handleClose = (markerId: string) => {
    if (selectedMarkerId === markerId) {
      setSelectedMarkerId(undefined);
    }
  };

  const handleMapClick = () => {
    setSelectedMarkerId(undefined);
  };

  const fitMapToLocations = React.useCallback(
    async (targetMap: google.maps.Map) => {
      const boundingBox = new google.maps.LatLngBounds();
      locationPurchaseHistory.forEach(({ location }) => {
        boundingBox.extend(location);
      });

      if (locationPurchaseHistory.length === 1) {
        const { lat, lng } = locationPurchaseHistory[0].location;
        // without manually zooming out, 1 location gets zoomed to the building level
        boundingBox.extend(new google.maps.LatLng(lat + 0.025, lng + 0.025));
        boundingBox.extend(new google.maps.LatLng(lat - 0.025, lng - 0.025));
      }

      if (_.isEmpty(locationPurchaseHistory)) {
        // if present, use patron zipcode to center map
        // else if present, use patron city to center map
        const address = !_.isEmpty(patronZip)
          ? patronZip
          : !_.isEmpty(patronCity)
            ? patronCity
            : undefined;
        if (address) {
          const geocodedZip = await getLatLong(address);
          const viewport = geocodedZip.geometry.viewport;
          boundingBox.extend(viewport.getNorthEast());
          boundingBox.extend(viewport.getSouthWest());
        } else {
          // else manually center around the united states
          boundingBox.extend(new google.maps.LatLng(50, -125));
          boundingBox.extend(new google.maps.LatLng(30, -67.5));
        }
      }
      targetMap.fitBounds(boundingBox);
    },
    [locationPurchaseHistory, patronZip, patronCity]
  );

  React.useEffect(() => {
    if (!map || hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;
    fitMapToLocations(map);
  }, [map, fitMapToLocations]);

  const fullScreenControlsContainer = (
    <div
      css={css`
        position: absolute;
        top: 0;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {map ? <FullscreenControl element={map.getDiv() as HTMLElement} /> : null}
    </div>
  );

  const zoomControlsContainer = (
    <div
      css={({ spacing }: Theme) => css`
        position: absolute;
        left: 0;
        bottom: ${spacing(3)}px;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {map ? <ZoomControls map={map} /> : null}
    </div>
  );

  if (loadError) {
    return <div>{String(loadError)}</div>;
  }

  return (
    <div className="ProfileMap" role="application">
      <Map
        mapId={MAP_ID}
        clickableIcons={false}
        disableDefaultUI={true}
        scrollwheel={false}
        keyboardShortcuts={false}
        defaultZoom={12}
        defaultCenter={DEFAULT_CENTER}
        onClick={handleMapClick}
        className="ProfileMap-container"
        reuseMaps
      >
        {fullScreenControlsContainer}
        {!isTouch ? zoomControlsContainer : null}
        {_.map(
          locationPurchaseHistory,
          ({
            location: baseLocation,
            business,
            purchases,
            indexWithinLocation,
          }: LocationPurchaseHistory) => {
            const location = offsetByDistance(baseLocation, 5 * indexWithinLocation, 45);
            const id = `${location.lat},${location.lng}`;
            const consumerPurchases: ConsumerPurchaseSummary[] = _.map(purchases, (purchase) => {
              return {
                business: purchase.business as BusinessEntity,
                city: purchase.city as string,
                country: purchase.country as string,
                dinerPatron: purchase.dinerPatron[0] as unknown as PatronEntity,
                firstTransferredTo: purchase.firstTransferredTo as PatronEntity,
                id: purchase.id,
                cancelledOrRefunded: purchase.cancelledOrRefunded,
                eligibleForFeedback: purchase.isEligibleForFeedback,
                ownerPatron: purchase.ownerPatron as PatronEntity,
                ticketCount: purchase.ticketCount,
                ticketDateTime: purchase.ticketDateTime,
                ticketType: purchase.ticketType as TicketTypeEntity,
                cancellationPolicyDescription: [],
                seatingAreaName: '',
                confirmationText: '',
                confirmationCode: '',
              };
            });
            return (
              <RestaurantMarkerNew
                id={id}
                isOpen={selectedMarkerId === id}
                location={location}
                onOpen={handleOpen}
                onClose={handleClose}
                business={business}
                purchases={consumerPurchases}
                key={id}
              />
            );
          }
        )}
      </Map>
    </div>
  );
};

function offsetByDistance(
  latLng: google.maps.LatLngLiteral,
  distanceMeters: number,
  bearingDeg: number
): google.maps.LatLngLiteral {
  // Ported from https://jsfiddle.net/kodisha/8a3hcjtd/
  const R = 6378100; // Radius of the Earth in meters
  const brng = (bearingDeg * Math.PI) / 180; // Convert bearing to radian
  let lat = (latLng.lat * Math.PI) / 180; // Current coords to radians
  let lon = (latLng.lng * Math.PI) / 180;

  lat = Math.asin(
    Math.sin(lat) * Math.cos(distanceMeters / R) +
      Math.cos(lat) * Math.sin(distanceMeters / R) * Math.cos(brng)
  );
  lon += Math.atan2(
    Math.sin(brng) * Math.sin(distanceMeters / R) * Math.cos(lat),
    Math.cos(distanceMeters / R) - Math.sin(lat) * Math.sin(lat)
  );

  return { lat: (lat * 180) / Math.PI, lng: (lon * 180) / Math.PI };
}

/**
 * vis.gl (flag-ON) replacement for `ProfileMap`. Self-wraps a `MapsApiProvider`
 * so parents are unchanged.
 */
const ProfileMapNew: React.FunctionComponent<ProfileMapNewProps> = (props) => (
  <MapsApiProvider>
    <ProfileMapController {...props} />
  </MapsApiProvider>
);

export default ProfileMapNew;
